<template>
  <div class="offers-form">
    <div class="row">
      <div class="col-md-6">
        <div class="mb-4">
          <card title="Clinic">
            <label>Clinic <span class="text-danger">*</span></label>
            <el-select
              v-model="form.clinic"
              value-key="id"
              filterable
              clearable
              placeholder="Select clinic"
              no-data-text="No data"
              no-match-text="No match"
              style="width: 100%"
            >
              <el-option
                v-for="c in clinics"
                :key="c.id"
                :label="clinicLabel(c)"
                :value="c"
              />
            </el-select>
          </card>
        </div>

        <OfferFormTextTabs
          :domains="domains"
          :fields="createFormFields"
          :form="form"
          :active-name.sync="activeName"
          :tab-label="tabLabel"
          :field-label="fieldLabel"
        />

        <OfferFormLinkedCatalog
          :form="form"
          :operations="operations"
          :illnesses="illnesses"
          :catalog-entity-label="catalogEntityLabel"
        />

        <div class="offers-form__actions mt-4">
          <button class="btn btn-primary" type="button" @click="create">Create Offer</button>
          <router-link :to="{ name: 'offers-list' }" class="btn btn-default">
            Cancel
          </router-link>
        </div>
      </div>

      <div class="col-md-6">
        <div class="mb-4">
          <OfferFormSettingsCard
            :form="form"
            :tags-input.sync="tagsInput"
            :currencies="currencies"
            :level-options="levelOptions"
          />
        </div>

        <div class="mb-4">
          <OfferFormMedicalServicesCard :form="form" :grouped-services="groupedServices">
            Pick medical, accommodation, transfer, common, implant services, etc. in one place. Catalog:
            <router-link :to="{ name: 'offer-services-list' }">Offer services</router-link>.
          </OfferFormMedicalServicesCard>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import APIClinics from "@/api/modules/clinics";
import APICmsCatalogs from "@/api/modules/cms-api-catalogs";
import OfferFormTextTabs from "@/components/Offers/OfferFormTextTabs.vue";
import OfferFormSettingsCard from "@/components/Offers/OfferFormSettingsCard.vue";
import OfferFormMedicalServicesCard from "@/components/Offers/OfferFormMedicalServicesCard.vue";
import OfferFormLinkedCatalog from "@/components/Offers/OfferFormLinkedCatalog.vue";
import { API_FIELD_MAP, LEVEL_OPTIONS, SERVICE_GROUP_LABELS, TRANSLATABLE_FIELDS } from "./offerFormConstants";
import {
  buildOfferCreatePayload,
  clinicPayloadForOffer,
  emptyLangs,
  findCountryRowByGeoCode
} from "./offerPayload";
import {
  groupOfferServices,
  offerCatalogEntityLabel,
  offerDomainLanguages,
  offerFieldLabel,
  offerTabLabel
} from "./offerFormUi";

export default {
  components: {
    OfferFormTextTabs,
    OfferFormSettingsCard,
    OfferFormMedicalServicesCard,
    OfferFormLinkedCatalog
  },
  data: () => ({
    activeName: null,
    tagsInput: "",
    levelOptions: LEVEL_OPTIONS,
    translatableFields: TRANSLATABLE_FIELDS,
    form: {
      clinic: null,
      type: "procedure",
      level: "1",
      is_hide: false,
      template_id: "",
      score: null,
      name: {},
      for_who: {},
      duration: {},
      procedure_description: {},
      accommodation: {},
      transport: {},
      other_services: {},
      post_operation_care: {},
      implant: {},
      promo_description: {},
      services: [],
      packagePrice: null,
      currency_code: "",
      operations: [],
      illnesses: []
    }
  }),
  computed: {
    ...mapState("clinics", ["clinics"]),
    ...mapState("offerServices", ["servicesList"]),
    ...mapState("cmsApiCatalogs", ["domains", "countries", "operations", "illnesses"]),
    ...mapState("data", ["currencies"]),
    domainLanguages() {
      return offerDomainLanguages(this.domains);
    },
    groupedServices() {
      return groupOfferServices(this.servicesList, SERVICE_GROUP_LABELS);
    },
    createFormFields() {
      const editOnly = new Set([
        "post_operation_care",
        "accommodation",
        "transport",
        "other_services",
        "implant"
      ]);
      return this.translatableFields.filter((f) => !editOnly.has(f.key));
    }
  },
  watch: {
    "form.type"(next, prev) {
      if (next === prev || prev === undefined) return;
      if (next === "procedure") {
        this.form.illnesses = [];
      } else if (next === "illness") {
        this.form.operations = [];
      }
    }
  },
  methods: {
    ...mapActions("offers", ["createOffer"]),
    ...mapActions("clinics", ["getClinics"]),
    ...mapActions("offerServices", ["getServices"]),
    ...mapActions("cmsApiCatalogs", ["getDomains", "getCountries", "getOperations", "getIllnesses"]),
    ...mapActions("data", ["getCurrencies"]),
    tabLabel: offerTabLabel,
    fieldLabel: offerFieldLabel,
    catalogEntityLabel: offerCatalogEntityLabel,
    clinicLabel(c) {
      if (!c) return "";
      const name = c.name && (c.name.en || c.name.ru);
      return name || String(c.id);
    },
    resetLangFields() {
      const defaults = emptyLangs(this.domainLanguages);
      Object.keys(API_FIELD_MAP).forEach((field) => {
        this.$set(this.form, field, { ...defaults, ...(this.form[field] || {}) });
      });
      if (!this.domains || !this.domains.length) return;
      const names = this.domains.map((d) => offerTabLabel(d.language));
      if (!names.includes(this.activeName)) {
        this.activeName = names[0];
      }
    },
    async create() {
      if (!this.form.clinic || !this.form.clinic.id) {
        return this.$notify({ type: "warning", title: "Clinic is required" });
      }
      if (!this.form.name.en) {
        return this.$notify({ type: "warning", title: "Name EN is required" });
      }
      if (!this.form.type) {
        return this.$notify({ type: "warning", title: "Type is required" });
      }

      let resolvedClinic = this.form.clinic;
      this.$store.dispatch("setLoader", true);
      try {
        const res = await APIClinics.getClinic(this.form.clinic.id);
        if (res && res.data && res.data.clinic) {
          resolvedClinic = res.data.clinic;
        }
      } catch (e) {}

      const geo = resolvedClinic.geo;
      let countryRow = null;
      let cityRow = null;

      if (!this.countries.length) {
        await this.getCountries({ perPage: 1500 });
      }
      if (geo && geo.country_code) {
        countryRow = findCountryRowByGeoCode(this.countries, geo.country_code);
        if (!countryRow) {
          try {
            const cr = await APICmsCatalogs.getCountries({ perPage: 1500 });
            countryRow = findCountryRowByGeoCode(cr?.data?.countries || [], geo.country_code);
          } catch (e) {}
        }
      }

      if (geo && geo.city_id != null) {
        try {
          const cr = await APICmsCatalogs.getCity(geo.city_id);
          cityRow = cr?.data?.city || cr?.data;
        } catch (e) {}
      }

      const clinicPayload = clinicPayloadForOffer(resolvedClinic, { countryRow, cityRow });
      if (!clinicPayload) {
        this.$store.dispatch("setLoader", false);
        return this.$notify({ type: "warning", title: "Invalid clinic" });
      }
      if (!clinicPayload.country) {
        this.$store.dispatch("setLoader", false);
        return this.$notify({
          type: "warning",
          title:
            "Country is required: add nested country on clinic or set geo.country_code and ensure it exists in /data/countries"
        });
      }

      if (!this.currencies.length) {
        await this.getCurrencies();
      }

      const payload = buildOfferCreatePayload(
        this.form,
        this.tagsInput,
        this.domainLanguages,
        clinicPayload,
        this.currencies
      );

      try {
        const response = await this.createOffer(payload);
        const id = response.data?.offer?.id;
        this.$notify({ type: "success", title: "Offer created" });
        if (id) {
          this.$router.push({ name: "offers-edit", params: { id } });
        } else {
          this.$router.push({ name: "offers-list" });
        }
      } catch (e) {
        this.$notify({ type: "warning", title: "Error creating offer" });
      } finally {
        this.$store.dispatch("setLoader", false);
      }
    }
  },
  async created() {
    this.$store.dispatch("setLoader", true);
    try {
      await Promise.all([
        this.getDomains(),
        this.getClinics({ perPage: 1500 }),
        this.getCountries({ perPage: 1500 }),
        this.getServices({}),
        this.getCurrencies(),
        this.getOperations(),
        this.getIllnesses({ perPage: 1500 })
      ]);
      if (this.domains.length) {
        this.activeName = offerTabLabel(this.domains[0].language);
      }
      this.resetLangFields();
    } finally {
      this.$store.dispatch("setLoader", false);
    }
  }
};
</script>

<style scoped>
.offers-form__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
</style>
